import prizeTag from "@assets/web-v3-images/prize-tag.png";
// import { ImagePlaceholder } from "@/component";
import IntroTourHome from "@/page/components/components/IntroTourHome";
import PATH from "@config/PATH";
import { PrizeType } from "@types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import styled, { keyframes } from "styled-components";
import CountdownBar from './CountdownBar';
/** Khung ảnh giải: dùng chung cho <img> và div giữ chỗ khi không có ảnh, để hai
 *  nhánh luôn chiếm đúng một chỗ như nhau.
 *
 *  aspect theo tỉ lệ FILE (518×594), không phải 403/499 của design: 403/499 là
 *  tỉ lệ thân máy, mà thân máy chỉ chiếm 61%×66% file do PNG có viền glow trong
 *  suốt. Giữ khung = tỉ lệ file thì thân máy bên trong tự ra đúng 403/499; ép
 *  403/499 lên cả file sẽ letterbox và đẩy thân máy tụt ~3.2vw khỏi bục. */
// Cap các trị vw theo BỀ RỘNG KHUNG thật = 768px (.main-layout-website), không
// phải 880px như trước -> ảnh giải vừa khít khung trên desktop:
//   max-w 537 = 70%×768 · -mx cap 77 = 10%×768 · (translate 11.4vw cap 87.6 = 11.4%×768)
const PrizeFrame = styled.div`
  position: relative;
  aspect-ratio: 518 / 594;
  width: 70vw;
  max-width: 537px;
  margin-inline: -10vw;

  @media (min-width: 768px) {
    margin-inline: calc(-1 * min(10vw, 77px));
  }
`;

// Nhịp chuyển ảnh trong slideshow (ms).
const PRIZE_SLIDE_MS = 3000;

const ShowcaseGrid = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
`;

const PrizeStage = styled.div`
  position: relative;
  pointer-events: none;
  transform: translateY(
    calc(11.4vw - ${({ $isDailyReward }) => ($isDailyReward ? "74px" : "121px")})
  );
`;

/* absolute thay vì relative: top % của phần tử relative tính theo chiều cao
   khung cha, mà chiều cao PrizeFrame chỉ đến từ aspect-ratio — Safari (iOS) coi
   đó là chiều cao "auto" nên top % bị bỏ (= 0) và ảnh nhảy lên trên. Với
   absolute, top % luôn tính theo kích thước thật của khung. */
const PrizeSlides = styled.div`
  position: absolute;
  top: 37%;
  left: 0;
  width: 100%;
  aspect-ratio: 518 / 280;

  @media (min-width: 640px) {
    top: 19%;
    aspect-ratio: 518 / 340;
  }
`;

const PrizeImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 700ms cubic-bezier(0.4, 0, 0.2, 1);
`;

/* Tag giá đưa qua trái–phải rồi tắt dần về đúng vị trí ban đầu, nghỉ một nhịp
   ngắn rồi lặp lại: toàn bộ cú lắc gói trong 0–50% (2s), 50–100% là quãng nghỉ. */
const tagSwing = keyframes`
  0% { transform: rotate(0deg); }
  8% { transform: rotate(-5deg); }
  20% { transform: rotate(4deg); }
  32% { transform: rotate(-2.5deg); }
  41% { transform: rotate(1.2deg); }
  50%, 100% { transform: rotate(0deg); }
`;

const PriceTag = styled.div`
  container-type: inline-size;
  position: absolute;
  right: -40%;
  bottom: 13%;
  width: 68%;
  aspect-ratio: 321 / 256;
  transform-origin: 47% 8%;
  animation: ${tagSwing} 4s ease-in-out infinite;

  > img {
    width: 100%;
    height: 100%;
  }
`;

const PriceTagContent = styled.div`
  position: absolute;
  inset: 0 6%;
  display: grid;
  place-content: center;
  gap: 6px;
  padding-top: 24%;
  text-align: center;
  transform: rotate(-7.5deg);
`;

const PrizeName = styled.p`
  overflow-wrap: break-word;
  font-size: 6.5cqw;
  font-weight: 800;
  line-height: 1.25;
  color: #fef0c7;
`;

const PrizePrice = styled.p`
  position: relative;
  white-space: nowrap;
  font-size: 10.5cqw;
  font-weight: 800;
  line-height: 1.25;
`;

const PrizePriceStroke = styled.span`
  position: absolute;
  inset: 0;
  color: #c33e00;
  -webkit-text-stroke: 0.31cqw #c33e00;
  text-shadow: 0 0.62cqw 1.1cqw #b11111;
`;

const PrizePriceFill = styled.span`
  position: relative;
  color: transparent;
  background-image: linear-gradient(to bottom, #fff8d2, #fceb7c 49%, #fff5bf);
  -webkit-background-clip: text;
  background-clip: text;
`;

/**
 * Khu vực trưng bày giải thưởng: nút History / Prize Pool hai bên, ảnh sản phẩm
 * ở giữa trên bục (podium), kèm tag giá treo bên phải.
 *
 * Bục nằm trong ảnh nền home-bg.png (bg-contain, rộng = 100vw) nên mọi kích
 * thước/khoảng cách ở đây tính theo vw để ảnh giải luôn đứng đúng trên bục
 * ở mọi cỡ màn hình.
 */
const PrizeShowcase = ({
  prize = PrizeType,
  isDailyReward = false,
  endsAt
}) => {
  const { t } = useTranslation();
  /* Swing của tag giá chỉ chạy ở màn home. Không suy ra được từ `isDailyReward`:
     HomePage (nơi render component này) được mount ở CẢ "/" lẫn "/bid/:id"
     (routerList.jsx) nên phải so trực tiếp pathname với PATH.HOME. */
  const { pathname } = useLocation();
  const isHome = pathname === PATH.HOME;
  /* prize.image là chuỗi nhiều link ảnh ngăn nhau bởi "," -> tách hết để chạy
     slideshow. Ảnh nào tải lỗi thì loại khỏi danh sách (lưu trong `failed`).
     Không còn ảnh hợp lệ thì fallback grandPrize để khung luôn có kích thước. */
  const [failed, setFailed] = useState({});
  const [index, setIndex] = useState(0);

  const images = useMemo(() => {
    const list = (prize?.image || "")
      .split(",")
      .map((s) => s.trim())
      .filter((src) => src && !failed[src]);
    return list.length ? list : [];
  }, [prize?.image, failed]);

  // Prize đổi -> quay về ảnh đầu và xoá cờ lỗi cũ.
  useEffect(() => {
    setIndex(0);
    setFailed({});
  }, [prize?.image]);

  // Tự chuyển ảnh (vòng lặp) khi có nhiều hơn 1 ảnh.
  useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = setInterval(
      () => setIndex((i) => (i + 1) % images.length),
      PRIZE_SLIDE_MS,
    );
    return () => clearInterval(id);
  }, [images.length]);

  const activeIndex = index % images.length;

  return (
    <>
      <IntroTourHome />
      <section className="relative">
        {/* Lưới 3 cột để ảnh giải luôn ở chính giữa trục bục, bất kể độ rộng 2 nút */}
        <ShowcaseGrid>
          {/* Ảnh giải + tag giá: dịch bằng calc(vw) để thân máy luôn đứng trên mặt
            bục trong home-bg.png (mặt bục ≈ 1.09 × chiều rộng màn hình tính từ
            đỉnh trang). PNG có lề trong suốt lớn (thân máy chỉ chiếm 61% ngang
            × 66% dọc file ảnh) nên ảnh render 70vw để thân máy ≈ 43% màn hình
            như mock, kèm margin âm ngang bù phần lề để không phá lưới 3 cột;
            hằng số px bù chiều cao cố định của header ở mỗi breakpoint.
            min() đóng băng phần vw từ 880px vì #root cap trang ở 880px. */}
          {/* mb lặp lại đúng calc của translate-y: translate chỉ dịch phần nhìn
            thấy, không đổi chiều cao layout — mb đồng bộ hai thứ để nội dung
            phía sau (GuessPriceCard) luôn nằm sát dưới bục ở mọi cỡ màn. */}
          <PrizeStage $isDailyReward={isDailyReward}>
            {/* Không có ảnh (hoặc ảnh lỗi) thì giữ chỗ bằng div nền trong suốt cùng
              khung: tag giá định vị theo % của khối này nên khung phải luôn có
              kích thước, nếu không tag sẽ rơi lên bục. */}
            {/* Slideshow: xếp chồng tất cả ảnh trong cùng khung, ảnh đang hiện
              fade-in (opacity 1), còn lại ẩn. Khung (PrizeFrame) giữ
              kích thước, ảnh phủ absolute inset-0. */}
            <PrizeFrame>
              <PrizeSlides>
                {images.map((src, i) => (
                  <PrizeImage
                    key={`${src}-${i}`}
                    src={src}
                    alt="prize"
                    $active={i === activeIndex}
                    onError={() => setFailed((f) => ({ ...f, [src]: true }))}
                  />
                ))}
              </PrizeSlides>
            </PrizeFrame>


            {/* Tag giá: nền prize-tag.png (pin + dây + biển nghiêng có sẵn trong
              ảnh). Kích thước/vị trí theo % của khối ảnh giải nên tự co giãn:
              pin ghim vào mép phải thân máy, biển treo ở góc dưới phải như mock.
              Chữ xoay -6° theo độ nghiêng của tấm biển trong ảnh. */}
            {
              !isDailyReward && (
                /* origin đặt ở ĐẦU GHIM (pin nằm ~47% ngang, ~8% dọc của
                   prize-tag.png) để tag đảo qua lại như biển treo trên đinh; để
                   mặc định origin center thì nó xoay quanh tâm biển, trông như
                   đang bị lắc ngang chứ không phải treo. */
                <PriceTag>
                  <img src={prizeTag} alt="" />
                  <PriceTagContent>
                    {/* Tên giải dài thì cho xuống dòng (bỏ whitespace-nowrap); break-words
                  để từ quá dài cũng ngắt được, không tràn khỏi biển tag. */}
                    <PrizeName>{prize.name}</PrizeName>
                    <PrizePrice>
                      <PrizePriceStroke aria-hidden="true">
                        {prize.price.toLocaleString("en-US")} MMK
                      </PrizePriceStroke>
                      <PrizePriceFill>
                        {prize.price.toLocaleString("en-US")} MMK
                      </PrizePriceFill>
                    </PrizePrice>
                  </PriceTagContent>
                </PriceTag>
              )
            }
          </PrizeStage>
        </ShowcaseGrid>
      </section>
      <div className="relative -top-14 sm:top-10">
        <CountdownBar endsAt={endsAt} />
      </div>
    </>
  );
};

export default PrizeShowcase;
